import React, { useState, useMemo } from 'react';
import ShowcasePageWrapper from '../shared/ShowcasePageWrapper';
import { generateAccounts, generateActivityFeed } from '../shared/SampleData';
import Button from '@salesforce/design-system-react/components/button';
import ButtonGroup from '@salesforce/design-system-react/components/button-group';
import Icon from '@salesforce/design-system-react/components/icon';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import Badge from '@salesforce/design-system-react/components/badge';
import Avatar from '@salesforce/design-system-react/components/avatar';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import Toast from '@salesforce/design-system-react/components/toast';
import ToastContainer from '@salesforce/design-system-react/components/toast/container';

// -- Style constants ----------------------------------------------------------

const cardStyle = {
  backgroundColor: 'white',
  border: '1px solid #dddbda',
  borderRadius: '4px',
  padding: '20px',
};

const statusColors = {
  Active: '#2e844a',
  Inactive: '#706e6b',
  Pending: '#fe9339',
  'At Risk': '#c23934',
  Churned: '#514f4d',
};

const ratingColors = {
  Hot: '#c23934',
  Warm: '#fe9339',
  Cold: '#0176d3',
};

const activityIconMap = {
  call: { name: 'call', category: 'standard', color: '#2e844a' },
  email: { name: 'email', category: 'standard', color: '#0176d3' },
  meeting: { name: 'event', category: 'standard', color: '#9050e9' },
  task: { name: 'task', category: 'standard', color: '#fe9339' },
  stage_change: { name: 'opportunity', category: 'standard', color: '#c23934' },
};

const fileIconColors = {
  pdf: '#c23934',
  docx: '#0176d3',
  xlsx: '#2e844a',
  pptx: '#fe9339',
  png: '#9050e9',
};

// -- Helper generators --------------------------------------------------------

const firstNames = ['Sarah', 'James', 'Maria', 'Robert', 'Emily', 'Michael', 'Jessica', 'David', 'Ashley', 'Daniel'];
const lastNames = ['Johnson', 'Chen', 'Garcia', 'Williams', 'Patel', 'Kim', 'Anderson', 'Martinez', 'Thompson', 'Lee'];
const titles = ['VP of Sales', 'Account Executive', 'CFO', 'Director of Engineering', 'Marketing Manager', 'CTO', 'Product Manager', 'COO', 'Sales Director', 'Head of Operations'];
const oppNames = ['Enterprise License', 'Platform Upgrade', 'Support Renewal', 'Analytics Add-on', 'Custom Integration', 'Data Migration', 'Training Package', 'API Access'];
const oppStages = ['Prospecting', 'Qualification', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];

const randomItem = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randomBetween = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const formatCurrency = (val) => '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const generateContacts = (accountName, count) => {
  return Array.from({ length: count }, (_, i) => {
    const first = randomItem(firstNames);
    const last = randomItem(lastNames);
    return {
      id: `contact-${accountName}-${i}`,
      name: `${first} ${last}`,
      firstName: first,
      lastName: last,
      title: randomItem(titles),
      email: `${first.toLowerCase()}.${last.toLowerCase()}@${accountName.toLowerCase().replace(/\s+/g, '')}.com`,
      phone: `(${randomBetween(200, 999)}) ${randomBetween(200, 999)}-${randomBetween(1000, 9999)}`,
    };
  });
};

const generateOpportunities = (accountName, count) => {
  return Array.from({ length: count }, (_, i) => {
    const stage = randomItem(oppStages);
    return {
      id: `opp-${accountName}-${i}`,
      name: `${accountName} - ${randomItem(oppNames)}`,
      amount: randomBetween(10000, 500000),
      stage,
      closeDate: `2025-${String(randomBetween(1, 12)).padStart(2, '0')}-${String(randomBetween(1, 28)).padStart(2, '0')}`,
      probability: stage === 'Closed Won' ? 100 : stage === 'Closed Lost' ? 0 : randomBetween(10, 90),
    };
  });
};

const generateFiles = () => [
  { id: 'file-1', name: 'Proposal_Q4_2025.pdf', ext: 'pdf', size: '2.4 MB', uploadedBy: 'Sarah Johnson', uploadedDate: '2025-06-15' },
  { id: 'file-2', name: 'Master_Service_Agreement.docx', ext: 'docx', size: '1.1 MB', uploadedBy: 'James Chen', uploadedDate: '2025-05-20' },
  { id: 'file-3', name: 'Financial_Summary.xlsx', ext: 'xlsx', size: '845 KB', uploadedBy: 'Maria Garcia', uploadedDate: '2025-06-01' },
  { id: 'file-4', name: 'Quarterly_Review_Deck.pptx', ext: 'pptx', size: '5.7 MB', uploadedBy: 'Robert Williams', uploadedDate: '2025-04-10' },
  { id: 'file-5', name: 'Architecture_Diagram.png', ext: 'png', size: '320 KB', uploadedBy: 'Emily Patel', uploadedDate: '2025-06-22' },
  { id: 'file-6', name: 'NDA_Signed.pdf', ext: 'pdf', size: '190 KB', uploadedBy: 'David Kim', uploadedDate: '2025-03-14' },
];

// -- Custom DataTable cells ---------------------------------------------------

const ContactNameCell = ({ children, item }) => (
  <DataTableCell title={children}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Avatar
        variant="user"
        label={item.name}
        size="small"
      />
      <button type="button" onClick={(e) => e.preventDefault()} style={{ color: '#0176d3', textDecoration: 'none', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}>
        {children}
      </button>
    </div>
  </DataTableCell>
);
ContactNameCell.displayName = DataTableCell.displayName;

const OppStageCell = ({ children }) => {
  const stageColors = {
    Prospecting: '#706e6b',
    Qualification: '#0176d3',
    Proposal: '#fe9339',
    Negotiation: '#9050e9',
    'Closed Won': '#2e844a',
    'Closed Lost': '#c23934',
  };
  return (
    <DataTableCell title={children}>
      <Badge
        content={children}
        style={{
          backgroundColor: `${stageColors[children] || '#706e6b'}20`,
          color: stageColors[children] || '#706e6b',
          border: `1px solid ${stageColors[children] || '#706e6b'}40`,
        }}
      />
    </DataTableCell>
  );
};
OppStageCell.displayName = DataTableCell.displayName;

const CurrencyCell = ({ children }) => (
  <DataTableCell title={children}>
    <span style={{ fontWeight: '600' }}>{formatCurrency(children)}</span>
  </DataTableCell>
);
CurrencyCell.displayName = DataTableCell.displayName;

// -- Sub-components -----------------------------------------------------------

const RecordListItem = ({ account, isSelected, onSelect }) => {
  const statusColor = statusColors[account.status] || '#706e6b';
  return (
    <div
      onClick={() => onSelect(account.id)}
      style={{
        padding: '12px 16px',
        cursor: 'pointer',
        borderLeft: isSelected ? '4px solid #0176d3' : '4px solid transparent',
        backgroundColor: isSelected ? '#eef4ff' : 'white',
        borderBottom: '1px solid #f3f3f3',
        transition: 'background-color 0.15s ease',
      }}
      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f3f3f3'; }}
      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'white'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: '700', fontSize: '14px', color: '#080707', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {account.name}
          </div>
          <div style={{ fontSize: '12px', color: '#706e6b', marginBottom: '4px' }}>
            {account.industry}
          </div>
        </div>
        <Badge
          content={account.status}
          style={{
            backgroundColor: `${statusColor}15`,
            color: statusColor,
            border: `1px solid ${statusColor}40`,
            fontSize: '11px',
            flexShrink: 0,
          }}
        />
      </div>
      <div style={{ fontSize: '12px', color: '#706e6b', marginTop: '4px' }}>
        {formatCurrency(account.revenue)}
      </div>
    </div>
  );
};

const DetailField = ({ label, value, isEditing, editValue, onChange, type = 'text' }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{ fontSize: '12px', color: '#706e6b', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </div>
    {isEditing ? (
      <Input
        value={editValue !== undefined ? editValue : value}
        onChange={(e, { value: v }) => onChange && onChange(v)}
        type={type}
      />
    ) : (
      <div style={{ fontSize: '14px', color: '#080707', padding: '8px 0' }}>
        {value || '--'}
      </div>
    )}
  </div>
);

const ActivityItem = ({ activity }) => {
  const iconInfo = activityIconMap[activity.type] || activityIconMap.task;
  const timestamp = new Date(activity.timestamp);
  const timeStr = timestamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' at ' + timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  let description = '';
  switch (activity.type) {
    case 'call':
      description = `${activity.outcome} - ${activity.duration}. ${activity.notes || ''}`;
      break;
    case 'email':
      description = `Subject: "${activity.subject}" ${activity.opened ? '(Opened)' : '(Not opened)'}`;
      break;
    case 'meeting':
      description = `${activity.title} - ${activity.duration} with ${activity.attendees ? activity.attendees.length : 0} attendees`;
      break;
    case 'task':
      description = `${activity.title} - Due ${activity.dueDate} [${activity.priority}] ${activity.completed ? '(Completed)' : '(Pending)'}`;
      break;
    case 'stage_change':
      description = `${activity.opportunity}: ${activity.fromStage} → ${activity.toStage} (${formatCurrency(activity.amount)})`;
      break;
    default:
      description = 'Activity recorded';
  }

  return (
    <div style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid #f3f3f3' }}>
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        backgroundColor: `${iconInfo.color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon category={iconInfo.category} name={iconInfo.name} size="x-small" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#080707', textTransform: 'capitalize' }}>
            {activity.type === 'stage_change' ? 'Stage Change' : activity.type}
          </span>
          <span style={{ fontSize: '11px', color: '#706e6b' }}>{timeStr}</span>
        </div>
        <div style={{ fontSize: '12px', color: '#706e6b', marginBottom: '2px' }}>
          {activity.user}
        </div>
        <div style={{ fontSize: '13px', color: '#3e3e3c', lineHeight: '1.4' }}>
          {description}
        </div>
      </div>
    </div>
  );
};

const FileRow = ({ file, onDownload }) => {
  const iconColor = fileIconColors[file.ext] || '#706e6b';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #f3f3f3',
        gap: '12px',
      }}
    >
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '4px',
        backgroundColor: `${iconColor}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon category="doctype" name={file.ext} size="small" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#0176d3', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.name}
        </div>
        <div style={{ fontSize: '12px', color: '#706e6b' }}>
          {file.size} &middot; Uploaded by {file.uploadedBy} on {file.uploadedDate}
        </div>
      </div>
      <Button
        label="Download"
        variant="outline-brand"
        iconCategory="utility"
        iconName="download"
        iconPosition="left"
        onClick={() => onDownload && onDownload(file)}
      />
    </div>
  );
};

// -- Tab content components ---------------------------------------------------

const DetailsTab = ({ account, isEditing, editFields, onFieldChange, onEdit, onSave, onCancel }) => (
  <div style={{ padding: '16px 0' }}>
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
      {isEditing ? (
        <ButtonGroup>
          <Button label="Cancel" variant="neutral" onClick={onCancel} />
          <Button label="Save" variant="brand" onClick={onSave} />
        </ButtonGroup>
      ) : (
        <Button label="Edit" variant="neutral" iconCategory="utility" iconName="edit" iconPosition="left" onClick={onEdit} />
      )}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
      <DetailField
        label="Account Name"
        value={account.name}
        isEditing={isEditing}
        editValue={editFields.name}
        onChange={(v) => onFieldChange('name', v)}
      />
      <DetailField
        label="Industry"
        value={account.industry}
        isEditing={isEditing}
        editValue={editFields.industry}
        onChange={(v) => onFieldChange('industry', v)}
      />
      <DetailField
        label="Annual Revenue"
        value={formatCurrency(account.revenue)}
        isEditing={isEditing}
        editValue={editFields.revenue}
        onChange={(v) => onFieldChange('revenue', v)}
      />
      <DetailField
        label="Employees"
        value={String(account.employees)}
        isEditing={isEditing}
        editValue={editFields.employees}
        onChange={(v) => onFieldChange('employees', v)}
      />
      <DetailField
        label="Status"
        value={account.status}
        isEditing={isEditing}
        editValue={editFields.status}
        onChange={(v) => onFieldChange('status', v)}
      />
      <DetailField
        label="Account Owner"
        value={account.owner}
        isEditing={isEditing}
        editValue={editFields.owner}
        onChange={(v) => onFieldChange('owner', v)}
      />
      <DetailField
        label="Phone"
        value={account.phone}
        isEditing={isEditing}
        editValue={editFields.phone}
        onChange={(v) => onFieldChange('phone', v)}
      />
      <DetailField
        label="Website"
        value={account.website}
        isEditing={isEditing}
        editValue={editFields.website}
        onChange={(v) => onFieldChange('website', v)}
      />
      <DetailField
        label="Rating"
        value={account.rating}
        isEditing={isEditing}
        editValue={editFields.rating}
        onChange={(v) => onFieldChange('rating', v)}
      />
      <DetailField
        label="Last Activity"
        value={account.lastActivity}
        isEditing={isEditing}
        editValue={editFields.lastActivity}
        onChange={(v) => onFieldChange('lastActivity', v)}
      />
    </div>
  </div>
);

const RelatedTab = ({ account }) => {
  const contacts = useMemo(() => generateContacts(account.name, randomBetween(3, 5)), [account.name]);
  const opportunities = useMemo(() => generateOpportunities(account.name, randomBetween(2, 4)), [account.name]);

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Related Contacts */}
      <div style={{ ...cardStyle, marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon category="standard" name="contact" size="small" />
            <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#080707' }}>
              Contacts ({contacts.length})
            </h3>
          </div>
          <Button label="New Contact" variant="outline-brand" iconCategory="utility" iconName="add" iconPosition="left" />
        </div>
        <DataTable items={contacts} id="contacts-table">
          <DataTableColumn label="Name" property="name" width="200px">
            <ContactNameCell />
          </DataTableColumn>
          <DataTableColumn label="Title" property="title" />
          <DataTableColumn label="Email" property="email" />
          <DataTableColumn label="Phone" property="phone" width="160px" />
        </DataTable>
      </div>

      {/* Related Opportunities */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon category="standard" name="opportunity" size="small" />
            <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#080707' }}>
              Opportunities ({opportunities.length})
            </h3>
          </div>
          <Button label="New Opportunity" variant="outline-brand" iconCategory="utility" iconName="add" iconPosition="left" />
        </div>
        <DataTable items={opportunities} id="opportunities-table">
          <DataTableColumn label="Name" property="name" width="240px" />
          <DataTableColumn label="Amount" property="amount" width="130px">
            <CurrencyCell />
          </DataTableColumn>
          <DataTableColumn label="Stage" property="stage" width="140px">
            <OppStageCell />
          </DataTableColumn>
          <DataTableColumn label="Close Date" property="closeDate" width="130px" />
        </DataTable>
      </div>
    </div>
  );
};

const ActivityTab = ({ account }) => {
  const activities = useMemo(() => generateActivityFeed(5), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon category="standard" name="log_a_call" size="small" />
            <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#080707' }}>
              Recent Activity
            </h3>
          </div>
          <Button label="Log Activity" variant="outline-brand" iconCategory="utility" iconName="add" iconPosition="left" />
        </div>
        {/* Timeline line */}
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute',
            left: '17px',
            top: '12px',
            bottom: '12px',
            width: '2px',
            backgroundColor: '#dddbda',
          }} />
          {activities.map((activity) => (
            <ActivityItem key={activity.id} activity={activity} />
          ))}
        </div>
      </div>
    </div>
  );
};

const FilesTab = ({ account, onDownload }) => {
  const files = useMemo(() => generateFiles(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon category="standard" name="file" size="small" />
            <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#080707' }}>
              Files ({files.length})
            </h3>
          </div>
          <Button label="Upload File" variant="outline-brand" iconCategory="utility" iconName="upload" iconPosition="left" />
        </div>
        {files.map((file) => (
          <FileRow key={file.id} file={file} onDownload={onDownload} />
        ))}
      </div>
    </div>
  );
};

// -- Empty state --------------------------------------------------------------

const EmptyDetailState = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '400px',
    color: '#706e6b',
  }}>
    <div style={{
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#f3f3f3',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '16px',
    }}>
      <Icon category="utility" name="side_list" size="large" />
    </div>
    <h3 style={{ fontSize: '20px', fontWeight: '300', margin: '0 0 8px 0', color: '#3e3e3c' }}>
      Select a record from the list
    </h3>
    <p style={{ fontSize: '14px', margin: 0, color: '#706e6b' }}>
      Choose an account from the left panel to view its details
    </p>
  </div>
);

// -- Main component -----------------------------------------------------------

const MasterDetailView = () => {
  const accounts = useMemo(() => generateAccounts(20), []);

  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [isEditing, setIsEditing] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [toastMessage, setToastMessage] = useState(null);

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId) || null,
    [accounts, selectedAccountId]
  );

  const filteredAccounts = useMemo(() => {
    return accounts.filter((account) => {
      const matchesSearch = account.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'All' || account.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [accounts, searchTerm, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { All: accounts.length, Active: 0, Inactive: 0, 'At Risk': 0 };
    accounts.forEach((a) => {
      if (counts[a.status] !== undefined) {
        counts[a.status]++;
      }
    });
    return counts;
  }, [accounts]);

  const handleSelectAccount = (id) => {
    setSelectedAccountId(id);
    setIsEditing(false);
    setEditFields({});
  };

  const handleEdit = () => {
    if (!selectedAccount) return;
    setEditFields({
      name: selectedAccount.name,
      industry: selectedAccount.industry,
      revenue: String(selectedAccount.revenue),
      employees: String(selectedAccount.employees),
      status: selectedAccount.status,
      owner: selectedAccount.owner,
      phone: selectedAccount.phone,
      website: selectedAccount.website,
      rating: selectedAccount.rating,
      lastActivity: selectedAccount.lastActivity,
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFields({});
  };

  const handleSave = () => {
    setIsEditing(false);
    setToastMessage('Account record saved successfully.');
    setTimeout(() => setToastMessage(null), 4000);
  };

  const handleFieldChange = (field, value) => {
    setEditFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleDownload = (file) => {
    setToastMessage(`Downloading ${file.name}...`);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleDelete = () => {
    setToastMessage('Delete action triggered (demo only).');
    setTimeout(() => setToastMessage(null), 3000);
  };

  const filterButtons = ['All', 'Active', 'Inactive', 'At Risk'];

  return (
    <ShowcasePageWrapper
      title="Master-Detail View"
      description="Split-panel layout with searchable list and tabbed detail view"
      icon="side_list"
      iconCategory="utility"
    >
      {/* Toast container */}
      {toastMessage && (
        <ToastContainer>
          <Toast
            labels={{ heading: toastMessage }}
            variant="success"
            duration={4000}
            onRequestClose={() => setToastMessage(null)}
          />
        </ToastContainer>
      )}

      {/* Main split-panel layout */}
      <div style={{ display: 'flex', gap: '0', height: 'calc(100vh - 200px)', minHeight: '600px' }}>

        {/* ---- Left Panel: Record List ---- */}
        <div style={{
          width: '30%',
          minWidth: '280px',
          maxWidth: '400px',
          backgroundColor: 'white',
          border: '1px solid #dddbda',
          borderRadius: '4px 0 0 4px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Search */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #dddbda' }}>
            <Input
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e, { value }) => setSearchTerm(value)}
              iconLeft={
                <InputIcon
                  assistiveText={{ icon: 'Search' }}
                  name="search"
                  category="utility"
                />
              }
            />
          </div>

          {/* Status filter buttons */}
          <div style={{
            padding: '8px 16px',
            borderBottom: '1px solid #dddbda',
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap',
          }}>
            {filterButtons.map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: statusFilter === status ? '700' : '400',
                  color: statusFilter === status ? 'white' : '#706e6b',
                  backgroundColor: statusFilter === status ? '#0176d3' : '#f3f3f3',
                  border: statusFilter === status ? '1px solid #0176d3' : '1px solid #dddbda',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  outline: 'none',
                }}
              >
                {status} ({statusCounts[status] || 0})
              </button>
            ))}
          </div>

          {/* Record count */}
          <div style={{
            padding: '8px 16px',
            fontSize: '12px',
            color: '#706e6b',
            borderBottom: '1px solid #f3f3f3',
            backgroundColor: '#fafafa',
          }}>
            {filteredAccounts.length} {filteredAccounts.length === 1 ? 'record' : 'records'}
          </div>

          {/* Scrollable list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filteredAccounts.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: '#706e6b' }}>
                <Icon category="utility" name="search" size="small" />
                <p style={{ marginTop: '8px', fontSize: '13px' }}>No matching records found</p>
              </div>
            ) : (
              filteredAccounts.map((account) => (
                <RecordListItem
                  key={account.id}
                  account={account}
                  isSelected={account.id === selectedAccountId}
                  onSelect={handleSelectAccount}
                />
              ))
            )}
          </div>
        </div>

        {/* ---- Right Panel: Record Detail ---- */}
        <div style={{
          flex: 1,
          backgroundColor: 'white',
          border: '1px solid #dddbda',
          borderLeft: 'none',
          borderRadius: '0 4px 4px 0',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {!selectedAccount ? (
            <EmptyDetailState />
          ) : (
            <>
              {/* Detail page header */}
              <div style={{
                padding: '16px 24px',
                borderBottom: '1px solid #dddbda',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#fafafa',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '8px',
                    backgroundColor: '#0176d3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '700',
                    fontSize: '16px',
                  }}>
                    {selectedAccount.name.charAt(0)}
                  </div>
                  <div>
                    <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#080707' }}>
                      {selectedAccount.name}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                      <span style={{ fontSize: '13px', color: '#706e6b' }}>{selectedAccount.industry}</span>
                      <Badge
                        content={selectedAccount.status}
                        style={{
                          backgroundColor: `${statusColors[selectedAccount.status] || '#706e6b'}15`,
                          color: statusColors[selectedAccount.status] || '#706e6b',
                          border: `1px solid ${statusColors[selectedAccount.status] || '#706e6b'}40`,
                          fontSize: '11px',
                        }}
                      />
                      <Badge
                        content={selectedAccount.rating}
                        style={{
                          backgroundColor: `${ratingColors[selectedAccount.rating] || '#706e6b'}15`,
                          color: ratingColors[selectedAccount.rating] || '#706e6b',
                          border: `1px solid ${ratingColors[selectedAccount.rating] || '#706e6b'}40`,
                          fontSize: '11px',
                        }}
                      />
                    </div>
                  </div>
                </div>
                <ButtonGroup>
                  <Button
                    label="Edit"
                    variant="neutral"
                    iconCategory="utility"
                    iconName="edit"
                    iconPosition="left"
                    onClick={handleEdit}
                  />
                  <Button
                    label="Delete"
                    variant="destructive"
                    iconCategory="utility"
                    iconName="delete"
                    iconPosition="left"
                    onClick={handleDelete}
                  />
                </ButtonGroup>
              </div>

              {/* Key metrics bar */}
              <div style={{
                padding: '12px 24px',
                borderBottom: '1px solid #dddbda',
                display: 'flex',
                gap: '32px',
                backgroundColor: 'white',
              }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                    Revenue
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#080707' }}>
                    {formatCurrency(selectedAccount.revenue)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                    Employees
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#080707' }}>
                    {selectedAccount.employees.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                    Owner
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#080707' }}>
                    {selectedAccount.owner}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                    Growth
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: selectedAccount.growth >= 0 ? '#2e844a' : '#c23934',
                  }}>
                    {selectedAccount.growth >= 0 ? '+' : ''}{selectedAccount.growth}%
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>
                    Health Score
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '60px',
                      height: '6px',
                      backgroundColor: '#f3f3f3',
                      borderRadius: '3px',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${selectedAccount.health}%`,
                        height: '100%',
                        backgroundColor: selectedAccount.health >= 70 ? '#2e844a' : selectedAccount.health >= 40 ? '#fe9339' : '#c23934',
                        borderRadius: '3px',
                      }} />
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#080707' }}>
                      {selectedAccount.health}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tabbed content */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px 24px' }}>
                <Tabs id="detail-tabs">
                  <TabsPanel label="Details">
                    <DetailsTab
                      account={selectedAccount}
                      isEditing={isEditing}
                      editFields={editFields}
                      onFieldChange={handleFieldChange}
                      onEdit={handleEdit}
                      onSave={handleSave}
                      onCancel={handleCancelEdit}
                    />
                  </TabsPanel>
                  <TabsPanel label="Related">
                    <RelatedTab account={selectedAccount} />
                  </TabsPanel>
                  <TabsPanel label="Activity">
                    <ActivityTab account={selectedAccount} />
                  </TabsPanel>
                  <TabsPanel label="Files">
                    <FilesTab account={selectedAccount} onDownload={handleDownload} />
                  </TabsPanel>
                </Tabs>
              </div>
            </>
          )}
        </div>
      </div>
    </ShowcasePageWrapper>
  );
};

export default MasterDetailView;
